import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import {
  Clock, Play, Pause, RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Zap, History, Eye, Settings, Activity, TrendingUp, Bell
} from 'lucide-react';
import { toast } from 'sonner';

export default function CronDoadoresPage() {
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [status, setStatus] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [logs, setLogs] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statusRes, gapsRes, logsRes] = await Promise.all([
        apiClient.get('/admin/cron/doadores/status'),
        apiClient.get('/admin/cron/doadores/gaps-pendentes'),
        apiClient.get('/admin/cron/doadores/logs?limit=10')
      ]);
      
      setStatus(statusRes.data);
      setGaps(gapsRes.data.gaps || []);
      setLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do cron');
    } finally {
      setLoading(false);
    }
  };

  const handleExecutarManual = async () => {
    try {
      setExecuting(true);
      const res = await apiClient.post('/admin/cron/doadores/executar');
      toast.success(`Cron executado: ${res.data.propostas_criadas} propostas criadas`);
      loadData();
    } catch (error) {
      toast.error('Erro ao executar cron');
    } finally {
      setExecuting(false);
      setConfirmAction(null);
    }
  };

  const handleIniciarCron = async () => {
    try {
      await apiClient.post('/admin/cron/doadores/iniciar');
      toast.success('Cron iniciado');
      loadData();
    } catch (error) {
      toast.error('Erro ao iniciar cron');
    }
    setConfirmAction(null);
  };

  const handlePararCron = async () => {
    try {
      await apiClient.post('/admin/cron/doadores/parar');
      toast.success('Cron parado');
      loadData();
    } catch (error) {
      toast.error('Erro ao parar cron');
    }
    setConfirmAction(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-PT');
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cron-doadores-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Clock className="w-8 h-8 text-blue-500" />
            Cron de Doadores
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Sistema automático de detecção de gaps e geração de propostas
          </p>
        </div>
        <Button onClick={loadData} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Status Card */}
      <Card className={status?.running ? 'border-green-300 bg-green-50/50' : 'border-amber-300 bg-amber-50/50'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                status?.running ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                {status?.running ? (
                  <Activity className="w-8 h-8 text-green-600 animate-pulse" />
                ) : (
                  <Pause className="w-8 h-8 text-amber-600" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold">
                  {status?.running ? 'Cron Ativo' : 'Cron Inativo'}
                </h3>
                <p className="text-slate-500">
                  Intervalo: {status?.intervalo_minutos || 30} minutos
                </p>
                {status?.ultima_execucao && (
                  <p className="text-sm text-slate-400">
                    Última execução: {formatDate(status.ultima_execucao.created_at)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {status?.running ? (
                <Button 
                  variant="outline" 
                  onClick={() => setConfirmAction('parar')}
                  className="text-amber-600 hover:bg-amber-50"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Parar Cron
                </Button>
              ) : (
                <Button 
                  onClick={() => setConfirmAction('iniciar')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Cron
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => setConfirmAction('executar')}
                disabled={executing}
              >
                {executing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Executar Agora
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats da Última Execução */}
      {status?.ultima_execucao?.resultado && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <p className="text-2xl font-bold">{status.ultima_execucao.resultado.gaps_detectados}</p>
              <p className="text-sm text-slate-500">Gaps Detectados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold text-green-600">{status.ultima_execucao.resultado.propostas_criadas}</p>
              <p className="text-sm text-slate-500">Propostas Criadas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <XCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
              <p className="text-2xl font-bold text-red-600">{status.ultima_execucao.resultado.propostas_expiradas}</p>
              <p className="text-sm text-slate-500">Propostas Expiradas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Bell className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold text-blue-600">{status.ultima_execucao.resultado.followups_alertados}</p>
              <p className="text-sm text-slate-500">Follow-ups Criados</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gaps Pendentes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Gaps Críticos Pendentes ({gaps.length})
          </CardTitle>
          <CardDescription>
            Necessidades críticas sem stock que serão processadas na próxima execução
          </CardDescription>
        </CardHeader>
        <CardContent>
          {gaps.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recurso</TableHead>
                  <TableHead>Instituição</TableHead>
                  <TableHead>Distrito</TableHead>
                  <TableHead className="text-center">Quantidade</TableHead>
                  <TableHead className="text-center">Stock</TableHead>
                  <TableHead>Criticidade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gaps.map((gap, idx) => (
                  <TableRow key={gap.id || idx}>
                    <TableCell className="font-medium">{gap.tipo_recurso_nome}</TableCell>
                    <TableCell>{gap.instituicao_nome || '-'}</TableCell>
                    <TableCell>{gap.distrito_nome || 'Nacional'}</TableCell>
                    <TableCell className="text-center font-bold text-red-600">
                      {gap.quantidade_faltante}
                    </TableCell>
                    <TableCell className="text-center text-slate-500">
                      {gap.stock_disponivel || 0}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        gap.criticidade === 'Crítica' ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
                      }>
                        {gap.criticidade}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-300" />
              <p>Nenhum gap crítico pendente</p>
              <p className="text-sm">Todas as necessidades têm stock ou já têm propostas ativas</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico de Execuções */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Histórico de Execuções
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            {logs.length > 0 ? (
              <div className="space-y-3">
                {logs.map((log, idx) => (
                  <div key={log.id || idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">{formatDate(log.created_at)}</p>
                        <div className="flex gap-4 mt-1 text-xs text-slate-500">
                          <span>Gaps: {log.resultado?.gaps_detectados || 0}</span>
                          <span className="text-green-600">Propostas: {log.resultado?.propostas_criadas || 0}</span>
                          <span className="text-red-600">Expiradas: {log.resultado?.propostas_expiradas || 0}</span>
                          <span className="text-blue-600">Follow-ups: {log.resultado?.followups_alertados || 0}</span>
                        </div>
                      </div>
                      {log.resultado?.erros?.length > 0 && (
                        <Badge className="bg-red-100 text-red-800">
                          {log.resultado.erros.length} erros
                        </Badge>
                      )}
                    </div>
                    {log.resultado?.erros?.length > 0 && (
                      <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                        {log.resultado.erros.slice(0, 2).map((e, i) => (
                          <p key={i}>{e}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Nenhuma execução registada</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100">
                Como funciona o Cron de Doadores
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-disc list-inside">
                <li>Executa automaticamente a cada {status?.intervalo_minutos || 30} minutos</li>
                <li>Detecta necessidades críticas com menos de 50% do stock necessário</li>
                <li>Identifica doadores potenciais e gera propostas de contacto</li>
                <li>Cria alertas para propostas expiradas</li>
                <li>Marca contactos para follow-up após {3} dias sem resposta</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialogs */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'executar' && 'Executar Cron Manualmente'}
              {confirmAction === 'iniciar' && 'Iniciar Cron Automático'}
              {confirmAction === 'parar' && 'Parar Cron Automático'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'executar' && (
                <>
                  Isto irá executar o cron imediatamente e processar todos os gaps críticos pendentes.
                  {gaps.length > 0 && (
                    <span className="block mt-2 font-medium">
                      {gaps.length} gap(s) serão processados e propostas poderão ser criadas.
                    </span>
                  )}
                </>
              )}
              {confirmAction === 'iniciar' && 'O cron irá começar a executar automaticamente a cada 30 minutos.'}
              {confirmAction === 'parar' && 'O cron irá parar de executar automaticamente. Pode executar manualmente quando necessário.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={
              confirmAction === 'executar' ? handleExecutarManual :
              confirmAction === 'iniciar' ? handleIniciarCron :
              handlePararCron
            }>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
